import { Analytics } from '@cobliteam/analytics';
import { useTranslation } from '@cobliteam/i18n';
import { Button, Popover, Typography } from '@cobliteam/ui/components';
import { FormField, FormItem, useFormContext } from '@cobliteam/ui/form';
import Icon from '@cobliteam/ui/icons';
import type { DragControls } from 'motion/react';
import { useState } from 'react';
import type { FormValues } from '../../checklistSchema';
import ChecklistItemImageAllowed from './ChecklistItemImageAllowed';
import ChecklistItemOptions from './ChecklistItemOptions';
import ChecklistItemRequired from './ChecklistItemRequired';
import ChecklistItemTitle from './ChecklistItemTitle';
import ChecklistItemType from './ChecklistItemType';

interface ChecklistItemProps {
  index: number;
  removeItem: (index: number) => void;
  dragControls: DragControls;
}

const ChecklistItem = ({ index, removeItem, dragControls }: ChecklistItemProps) => {
  const { t } = useTranslation('checklists');
  const { control } = useFormContext<FormValues>();

  const [openPopover, setOpenPopover] = useState(false);

  return (
    <FormField control={control} name={`items.${index}`}>
      {() => (
        <FormItem className='flex w-full gap-1 rounded-md border border-gray-light bg-white py-4 pr-4 pl-1' data-testid='checklist-item'>
          <Button
            size='sm'
            variant='ghost'
            className='shrink-0 cursor-grab active:cursor-grabbing'
            icon={<Icon name='grip-vertical' color='secondary' />}
            onPointerDown={(e) => dragControls.start(e)}
            data-testid='checklist-item-drag-handle'
          />

          <div className='flex-1 space-y-2.5'>
            <div className='flex items-center justify-between gap-2.5'>
              <ChecklistItemType index={index} />

              <Popover open={openPopover} onOpenChange={setOpenPopover} modal={true}>
                <Popover.Trigger>
                  <Button variant='ghost' color='danger' size='sm' icon={<Icon name='trash-2' />} />
                </Popover.Trigger>

                <Popover.Content align='start' side='left' className='z-drawer mr-2.5 w-72'>
                  <Typography variant='description-title' className='mb-2.5 flex items-center gap-2.5'>
                    <Icon name='triangle-alert' color='danger' />
                    {t('delete_question_confirm')}
                  </Typography>

                  <Typography variant='caption-default'>{t('delete_question_warning')}</Typography>

                  <div className='mt-2.5 flex items-center gap-2.5'>
                    <Button variant='ghost' size='sm' className='flex-1' onClick={() => setOpenPopover(false)}>
                      {t('cancel')}
                    </Button>

                    <Button
                      size='sm'
                      color='danger'
                      className='flex-1'
                      onClick={() => {
                        Analytics.track('Checklist Item Deleted');
                        removeItem(index);
                      }}
                    >
                      {t('delete_question')}
                    </Button>
                  </div>
                </Popover.Content>
              </Popover>
            </div>

            <ChecklistItemTitle index={index} />
            <ChecklistItemOptions index={index} />

            <div className='flex items-center justify-between gap-5 pt-2.5'>
              <ChecklistItemImageAllowed index={index} />
              <ChecklistItemRequired index={index} />
            </div>
          </div>
        </FormItem>
      )}
    </FormField>
  );
};

export default ChecklistItem;
